import React, { useEffect, useState } from "react";
import { Card, Table, Badge, Spinner } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../../services/api";
import {
  Search,
  Person,
  Calendar,
  QuestionCircle,
  Envelope,
  Phone,
  Hash,
} from "react-bootstrap-icons";

const useQuery = () => {
  const location = useLocation();
  return new URLSearchParams(location.search);
};

const AdminSearchResults = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [results, setResults] = useState({
    students: [],
    questions: [],
    appointments: [],
  });

  const searchTerm = (query.get("q") || "").trim();

  useEffect(() => {
    const fetchResults = async () => {
      if (!searchTerm) {
        setResults({ students: [], questions: [], appointments: [] });
        return;
      }
      setLoading(true);
      setError("");
      try {
        const response = await api.get("/admin/search", {
          params: { q: searchTerm },
        });
        if (response.data.success) {
          setResults(
            response.data.data || {
              students: [],
              questions: [],
              appointments: [],
            },
          );
        } else {
          setError(response.data.error || "Search failed");
        }
      } catch (err) {
        console.error("Search error:", err);
        setError("Failed to perform search. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchResults();
  }, [searchTerm]);

  const pageStyle = {
    minHeight: "100vh",
    padding: "10px",
  };

  const cardStyle = {
    borderRadius: "15px",
    border: "none",
    boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
    background: "rgba(255,255,255,0.98)",
    marginBottom: "20px",
  };

  return (
    <div style={pageStyle} className="container-fluid">
      <div className="mb-4">
        <h2 className="mb-1 fw-bold d-flex align-items-center">
          <Search className="me-2" size={28} />
          Global Search
        </h2>
        <p className="text-muted mb-0">
          Results for:{" "}
          <span className="fw-semibold">"{searchTerm || "…"}"</span>
        </p>
      </div>

      {loading && (
        <Card style={cardStyle} className="mb-4">
          <Card.Body className="d-flex align-items-center justify-content-center py-4">
            <Spinner animation="border" variant="primary" className="me-3" />
            <span>Searching across students, questions and appointments…</span>
          </Card.Body>
        </Card>
      )}

      {error && !loading && (
        <Card style={cardStyle} className="mb-4">
          <Card.Body className="text-danger fw-semibold">{error}</Card.Body>
        </Card>
      )}

      {/* Students */}
      <Card style={cardStyle}>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="fw-bold mb-0 d-flex align-items-center">
              <Person className="me-2" />
              Students
            </h5>
            <Badge bg="primary" pill>
              {results.students.length} found
            </Badge>
          </div>
          {results.students.length === 0 ? (
            <div className="text-muted py-3">
              No students matching "{searchTerm}".
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="mb-0">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Student ID</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {results.students.map((s) => (
                    <tr
                      key={s._id}
                      style={{ cursor: "pointer" }}
                      onClick={() => navigate(`/admin/students/${s._id}`)}
                    >
                      <td>{s.name}</td>
                      <td>{s.email}</td>
                      <td>{s.phone}</td>
                      <td>{s.appointmentId}</td>
                      <td>
                        {s.createdAt
                          ? new Date(s.createdAt).toLocaleDateString()
                          : "N/A"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Questions */}
      <Card style={cardStyle}>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="fw-bold mb-0 d-flex align-items-center">
              <QuestionCircle className="me-2" />
              Questions
            </h5>
            <Badge bg="secondary" pill>
              {results.questions.length} found
            </Badge>
          </div>
          {results.questions.length === 0 ? (
            <div className="text-muted py-3">
              No questions matching "{searchTerm}".
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="mb-0">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Question</th>
                    <th>Category</th>
                    <th>Type</th>
                  </tr>
                </thead>
                <tbody>
                  {results.questions.map((q) => (
                    <tr
                      key={q._id}
                      style={{ cursor: "pointer" }}
                      onClick={() =>
                        navigate(
                          `/admin/questions?questionId=${encodeURIComponent(q._id)}`,
                        )
                      }
                    >
                      <td>{q.questionId}</td>
                      <td>{q.questionText}</td>
                      <td>{q.category || "-"}</td>
                      <td>{q.questionType || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Appointments */}
      <Card style={cardStyle}>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="fw-bold mb-0 d-flex align-items-center">
              <Calendar className="me-2" />
              Appointments
            </h5>
            <Badge bg="info" pill>
              {results.appointments.length} found
            </Badge>
          </div>
          {results.appointments.length === 0 ? (
            <div className="text-muted py-3">
              No appointments matching "{searchTerm}".
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="mb-0">
                <thead>
                  <tr>
                    <th>
                      <Hash className="me-1" />
                      Appointment ID
                    </th>
                    <th>
                      <Person className="me-1" />
                      Student
                    </th>
                    <th>
                      <Envelope className="me-1" />
                      Email
                    </th>
                    <th>
                      <Phone className="me-1" />
                      Phone
                    </th>
                    <th>Date</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {results.appointments.map((apt) => (
                    <tr
                      key={apt._id}
                      style={{ cursor: "pointer" }}
                      onClick={() => navigate(`/admin/appointments/${apt._id}`)}
                    >
                      <td>{apt.appointmentId}</td>
                      <td>{apt.studentId?.name || "N/A"}</td>
                      <td>{apt.studentId?.email || "N/A"}</td>
                      <td>{apt.studentId?.phone || "N/A"}</td>
                      <td>
                        {apt.scheduledDate
                          ? new Date(apt.scheduledDate).toLocaleDateString()
                          : "Not scheduled"}
                      </td>
                      <td>
                        <Badge
                          bg={
                            apt.status === "confirmed"
                              ? "success"
                              : apt.status === "pending"
                                ? "warning"
                                : apt.status === "completed"
                                  ? "info"
                                  : apt.status === "cancelled"
                                    ? "danger"
                                    : "secondary"
                          }
                          pill
                        >
                          {apt.status}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default AdminSearchResults;
